// Client adapters for the existing driver-availability / ride-request contracts.
// All matching, transactions, pricing, ownership and OTP checks remain on the server.
export type RadarPoint = { latitude: number; longitude: number };
export type RadarRequest = {
  id: number; fromLocation: string; fromLatitude?: number | null; fromLongitude?: number | null;
  toLocation: string; neededOn: string; seats: number; preferredVehicle?: string | null;
  contribution?: number | null; status: string; requestMode?: string | null;
  rideNowExpiresAt?: string | null; driverArrivedAt?: string | null; otpVerified?: boolean;
  paymentAmount?: number | null; contactUnlocked?: boolean;
  passenger: { id: number; name: string; mobile?: string };
  match?: { pickupDistanceKm?: number | null };
};
export type RadarVehicle = { type: string; name: string; number: string; radius: string };
export function isRadarPoint(point: unknown): point is RadarPoint {
  if (!point || typeof point !== "object") return false;
  const p = point as RadarPoint;
  return typeof p.latitude === "number" && Number.isFinite(p.latitude) && Math.abs(p.latitude) <= 90 &&
    typeof p.longitude === "number" && Number.isFinite(p.longitude) && Math.abs(p.longitude) <= 180 && !(p.latitude === 0 && p.longitude === 0);
}
export function validateRadarVehicle(vehicle: RadarVehicle) {
  if (!["Car", "Bike", "Scooter", "SUV"].includes(vehicle.type)) throw new Error("Select the actual vehicle type.");
  if (!vehicle.name.trim() || vehicle.name.trim().length > 80) throw new Error("Enter a vehicle model (up to 80 characters).");
  if (!vehicle.number.trim() || vehicle.number.trim().length > 30) throw new Error("Enter a vehicle number (up to 30 characters).");
  const radius = Number(vehicle.radius);
  if (!Number.isFinite(radius) || radius < 1 || radius > 200) throw new Error("Radius must be 1–200 km.");
  return radius;
}
export function liveRadarRequests(data: RadarRequest[], now = Date.now()) {
  return data.filter(item => item.requestMode === "Now" && item.status === "Open" &&
    (!item.rideNowExpiresAt || Date.parse(item.rideNowExpiresAt) > now));
}
export function radarAcceptPayload(vehicle: RadarVehicle, price: string, location: RadarPoint | null) {
  const radius = validateRadarVehicle(vehicle);
  if (!isRadarPoint(location)) throw new Error("Current driver GPS is required.");
  if (!price.trim() || !Number.isFinite(Number(price)) || Number(price) < 0 || Number(price) > 1000000) throw new Error("Enter a valid total ride price.");
  return { action: "accept", driverVehicleType: vehicle.type, acceptedVehicleType: vehicle.type,
    acceptedVehicleName: vehicle.name.trim(), acceptedVehicleNumber: vehicle.number.trim().toUpperCase(),
    matchedRideId: null, maxPickupDistanceKm: radius, acceptedPrice: Number(price),
    driverLatitude: location.latitude, driverLongitude: location.longitude };
}
// Serialize heartbeat and toggle writes so an already-running heartbeat finishes
// before Go Offline. Callers guard queued heartbeats before they begin.
export function createAvailabilityQueue() {
  let tail: Promise<unknown> = Promise.resolve();
  return function enqueue<T>(job: () => Promise<T>): Promise<T> {
    const result = tail.then(job);
    tail = result.catch(() => undefined);
    return result;
  };
}
